import { CanvasTilesCoords } from 'business/models/CustomModels';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  CustomLayerDTO,
  LayerDTO,
  LayerStateDTO,
  LayerTypeDTO,
} from 'business/models/Models';
import {
  getLinkedLayerGridDto,
  getUniqueLayerName,
} from 'business/plateviewer/GenerateUtil';
import { Plate, WellNavigationDetails } from 'components/Experiment/constants';
import Konva from 'konva';
import { DisplayModes, GetDisplayMode, GetPlatePadding, getNewId } from 'utils/Global';
import { initNewLayer } from 'utils/ExperimentUtils';

export const getDefaultVolume = (exp: ExperimentDtoClient) => {
  const defaultWellVol = exp?.defWellVolume;
  const defaultSeedVol = exp?.containerDefSeedVolume;
  let vol = defaultWellVol;
  if (exp?.layers.length === 0) {
    return defaultWellVol;
  } else {
    const concLayer = exp?.layers.find((layer) => layer.volumeBasedAddition == false);
    if (!concLayer) {
      return defaultWellVol;
    } else {
      switch (concLayer.type) {
        case LayerTypeDTO.Grid:
          vol = getLinkedLayerGridDto(exp?.layers, concLayer.layerDetail).startWellVolume;
          break;
        case LayerTypeDTO.Screen:
        case LayerTypeDTO.Protein:
          vol = concLayer.volume;
          break;
        case LayerTypeDTO.Random:
        case LayerTypeDTO.ConditionList:
          const customLayer = concLayer.layerDetail as CustomLayerDTO;
          vol = customLayer.wellVolume;
          break;
        case LayerTypeDTO.Seed:
          vol = defaultSeedVol;
          break;
        default:
          break;
      }
      return vol;
    }
  }
};

export const getWellsInRange = (startWell, endWell, exp: ExperimentDtoClient) => {
  if (startWell.x > endWell.x || startWell.colPos > endWell.colPos) {
    [startWell, endWell] = [endWell, startWell];
  }
  if (startWell.y > endWell.y || startWell.rowPos > endWell.rowPos) {
    [startWell, endWell] = [endWell, startWell];
  }
  const xDiff = endWell.x - startWell.x;
  const yDiff = endWell.y - startWell.y;
  const selectedWells: WellNavigationDetails[] = [];
  for (let column = startWell.y; column <= startWell.y + yDiff; column++) {
    const plateLocationOfWell = {
      rowPos: startWell.rowPos,
      colPos: startWell.colPos,
    };
    let startingColumnOfSelectedWells = 0;
    if (column + 1 > exp?.containerRows) {
      startingColumnOfSelectedWells = column % exp?.containerRows;
      plateLocationOfWell.rowPos = Math.floor(column / exp?.containerRows) + 1;
    } else {
      startingColumnOfSelectedWells = column;
    }

    const absoluteXAxisPosition =
      startWell.x > exp?.containerCols ? startWell.x % exp?.containerCols : startWell.x;
    const startingWellOfColumn =
      startingColumnOfSelectedWells * exp?.containerCols + absoluteXAxisPosition + 1;

    for (let row = 0; row <= xDiff; row++) {
      let wellNum = startingWellOfColumn + row;
      if (startWell.x + row + 1 > exp?.containerCols) {
        plateLocationOfWell.colPos = Math.floor(
          (startWell.x + row + 1) / exp?.containerCols,
        );
        let rowsToAdd = 0;
        if ((startWell.x + row + 1) % exp?.containerCols === 0) {
          rowsToAdd = exp?.containerCols;
        } else {
          rowsToAdd = (startWell.x + row + 1) % exp?.containerCols;
          plateLocationOfWell.colPos = plateLocationOfWell.colPos + 1;
        }
        wellNum = rowsToAdd + startingColumnOfSelectedWells * exp?.containerCols;
      }
      const plateOfWell = exp?.plateDto.find(
        (plate) =>
          plate.colPos == plateLocationOfWell.colPos &&
          plate.rowPos == plateLocationOfWell.rowPos,
      );
      const wellDetails: WellNavigationDetails = {
        wellNum,
        plateNum: plateOfWell.plateNumber,
      };
      if (plateOfWell?.plateId) {
        wellDetails.plateID = plateOfWell?.plateId;
      }
      selectedWells.push(wellDetails);
    }
  }

  return selectedWells;
};

const getTileLevel = (
  stx: number,
  endx: number,
  startr: number,
  endr: number,
  is1536WellPlate: boolean,
) => {
  if (is1536WellPlate) {
    if (endx - stx < 12) {
      return 2;
    }
    if (endx - stx < 24) {
      return 1;
    }
  } else {
    if ((endx - stx) * (endr - startr) < 48) {
      return 2;
    }
    if ((endx - stx) * (endr - startr) < 96) {
      return 1;
    }
  }
  return null;
};

export const getCanvasTilesCoordsForPlate = (
  plate: Plate,
  stage: Konva.Stage,
  scale: any,
  level: number,
  is1536WellPlate: boolean,
) => {
  const plateData: CanvasTilesCoords = {
    plateId: plate.plateId,
    stx: plate.stx,
    sty: plate.sty,
    endx: plate.endx,
    endy: plate.endy,
  };
  const cellWidth = plate.width / plate.cols;
  const cellHeight = plate.height / plate.rows;

  if (GetDisplayMode() === DisplayModes.DESIGN) {
    return;
  }

  let end = stage.x() + (plate.startX + plate.width) * scale;
  if (end > window.innerWidth) {
    end = window.innerWidth;
  }
  let xStartofPlate = stage.x() + plate.startX * scale;
  if (xStartofPlate < 0) {
    xStartofPlate = 0;
  }

  const startofCol = stage.x() + plate.startX * scale;
  const startofRow = stage.y() + plate.startY * scale;

  let row = stage.y() + (plate.startY + plate.height) * scale;
  if (row > window.innerHeight) {
    row = window.innerHeight;
  }
  let yStartofPlate = stage.y() + plate.startY * scale;
  if (yStartofPlate < 0) {
    yStartofPlate = 0;
  }
  let startcol = Math.round(
    plate.cols -
      (plate.width * scale - startofCol * (startofCol < 0 ? -1 : 0)) / scale / cellWidth,
  );
  let startrow = Math.round(
    plate.rows -
      (plate.height * scale - startofRow * (startofRow < 0 ? -1 : 0)) /
        scale /
        cellHeight,
  );
  let endcol = Math.round(startcol + (end - xStartofPlate) / scale / cellWidth);
  let endrow = Math.round(startrow + (row - yStartofPlate) / scale / cellHeight);
  if (startcol >= plate.cols || startrow >= plate.rows || endcol <= 0 || endrow <= 0) {
    startcol = 0;
    startrow = 0;
    endcol = 0;
    endrow = 0;
  }
  if (endcol > plate.cols) {
    endcol = plate.cols;
  }
  if (endrow > plate.rows) {
    endrow = plate.rows;
  }
  // at level = 1, divide canvas into 4x4 grid and load each grid's composite image which is in focus
  level = getTileLevel(startcol, endcol, startrow, endrow, is1536WellPlate);
  if (level === null) {
    return;
  }
  // add visible column and rows range in plate obj as retrieval later reqd in passing this info down
  plateData.stx = startcol;
  plateData.endx = endcol;
  plateData.sty = startrow;
  plateData.endy = endrow;

  return plateData;
};

//RFR: Move to utils
export const checkPlateForPoint = (x: number, y: number, exp: ExperimentDtoClient) => {
  const plates = exp?.plateDto;
  const colCount = exp?.colCount;

  for (let i = 0; i < plates?.length; i++) {
    const plate = plates[i];
    const rightBoundary = plate.startX + plate.width;
    const bottomBoundary = plate.startY + plate.height;
    const padding = GetPlatePadding() / 2;

    if (
      x >= plate.startX &&
      x <= rightBoundary &&
      y >= plate.startY &&
      y <= bottomBoundary
    ) {
      return i + 1;
    }

    if (
      x > rightBoundary &&
      x < rightBoundary + padding &&
      y >= plate.startY &&
      y <= bottomBoundary
    ) {
      return i + 1;
    }
    if (
      y > bottomBoundary &&
      y < bottomBoundary + padding &&
      x >= plate.startX &&
      x <= rightBoundary
    ) {
      return i + 1;
    }

    if (x > rightBoundary && (i + 1) % colCount === 0) {
      if (y >= plate.startY && y <= bottomBoundary) {
        return i + 1;
      }
    }

    if (y > bottomBoundary && i >= plates.length - colCount) {
      if (x >= plate.startX && x <= rightBoundary) {
        return i + 1;
      }
    }
  }

  return plates?.length;
};

export const Create2DArray = (rows) => {
  const arr = [];
  for (let i = 0; i < rows; i++) {
    arr[i] = [];
  }
  return arr;
};

export const addNewLayer = (
  x: number,
  y: number,
  width: number,
  height: number,
  type: string,
  exp: ExperimentDtoClient,
  expId: number,
) => {
  const newLayer = new LayerDTO();
  newLayer.id = getNewId();
  newLayer.x = x;

  newLayer.y = y;
  newLayer.width = width;
  newLayer.height = height;
  newLayer.volume = getDefaultVolume(exp);
  newLayer.targetDrop = -1;
  newLayer.overrideLayer = false;
  newLayer.locked = false;
  newLayer.expId = expId;
  newLayer.layerState = LayerStateDTO.New;
  newLayer.volumeBasedAddition = false;
  newLayer.dirty = true;
  const layerName = initNewLayer(
    type,
    newLayer,
    expId,
    getDefaultVolume(exp),
    exp?.layers,
    exp?.containerDrops?.length,
  );
  newLayer.name = getUniqueLayerName(exp?.layers, layerName);
  return newLayer;
};
